import { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { AlertTriangle, ShieldPlus, Users2, Trash2, Eye, X, Check } from "lucide-react";

/**
 * Admin → Cleanup
 *
 * Two flows on a single tab:
 *   1. CREATE NEW ADMIN — bootstrap a real admin account before purging the seed one.
 *      Calls POST /api/admin/users/create-admin {email, password, name}
 *
 *   2. PURGE ACCOUNTS — wipe every user except a "keep" list (and Baba + the caller).
 *      Calls POST /api/admin/users/cleanup-test-accounts?dry_run={bool}
 *      with body {keepEmails: [...], purgeAllNonKept: true}
 *
 *   The destructive button always opens a confirmation modal that requires
 *   typing DELETE to enable. Dry-run output is shown above so the operator
 *   can review exactly who will be deleted before committing.
 */
export default function AdminCleanup() {
  // ── Create-admin form
  const [newEmail, setNewEmail] = useState("");
  const [newPw, setNewPw] = useState("");
  const [newName, setNewName] = useState("Admin");
  const [creating, setCreating] = useState(false);

  // ── Cleanup state
  const [keepRaw, setKeepRaw] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTyped, setConfirmTyped] = useState("");
  const [result, setResult] = useState(null);

  const parseKeep = () => keepRaw.split(/[\s,]+/).map((e) => e.trim()).filter(Boolean);

  const createAdmin = async () => {
    if (!newEmail || !newPw) {
      toast.error("Email and password are required");
      return;
    }
    if (newPw.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post("/admin/users/create-admin", {
        email: newEmail,
        password: newPw,
        name: newName || "Admin",
      });
      toast.success(
        data.created
          ? `Admin created: ${data.email}`
          : `Existing user upgraded to ADMIN: ${data.email}`
      );
      setNewEmail("");
      setNewPw("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create admin");
    } finally {
      setCreating(false);
    }
  };

  const runPreview = async () => {
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post(
        "/admin/users/cleanup-test-accounts?dry_run=true",
        { keepEmails: parseKeep(), purgeAllNonKept: true }
      );
      setPreview(data);
      toast.success(`Preview ready — ${data.matched} accounts will be deleted`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally {
      setBusy(false);
    }
  };

  const execute = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        "/admin/users/cleanup-test-accounts?dry_run=false",
        { keepEmails: parseKeep(), purgeAllNonKept: true }
      );
      setResult(data);
      setPreview(null);
      setConfirmOpen(false);
      setConfirmTyped("");
      toast.success(`Deleted ${data.deleted} account(s) and cascaded across collections`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cleanup failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-cleanup">
      <div>
        <div className="label-mystic flex items-center gap-2"><Trash2 className="w-3 h-3" /> Cleanup</div>
        <h2 className="font-serif text-3xl mt-2">Reset accounts &amp; create real admins</h2>
        <p className="text-foreground/60 text-sm mt-1 max-w-2xl">
          Bootstrap a permanent admin account, then wipe every other user (seeds, QA,
          test signups) so you have a clean slate before real signups arrive.
          Cascade-deletes wallets, profiles, posts, bookings, services, notifications,
          and 10+ other downstream collections.
        </p>
      </div>

      {/* ────────────────────────────────────────────────────────────
          1. Create new admin
         ──────────────────────────────────────────────────────────── */}
      <div className="border border-secondary/15 bg-card/40 p-5 space-y-4">
        <div className="flex items-center gap-2">
          <ShieldPlus className="w-4 h-4 text-secondary" />
          <h3 className="font-serif text-xl">Create a real admin</h3>
        </div>
        <p className="text-foreground/60 text-sm">
          Skips the OTP flow. If the email already exists, that user is promoted to ADMIN
          and their password is reset to the value you enter here.
        </p>
        <div className="grid md:grid-cols-3 gap-3">
          <input
            type="email"
            placeholder="email@yourdomain.com"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            className="bg-background border border-secondary/20 text-foreground px-3 py-2 text-sm"
            data-testid="cleanup-new-admin-email"
          />
          <input
            type="text"
            placeholder="Display name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            className="bg-background border border-secondary/20 text-foreground px-3 py-2 text-sm"
            data-testid="cleanup-new-admin-name"
          />
          <input
            type="password"
            placeholder="Password (min 8 chars)"
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
            className="bg-background border border-secondary/20 text-foreground px-3 py-2 text-sm"
            data-testid="cleanup-new-admin-password"
          />
        </div>
        <button
          onClick={createAdmin}
          disabled={creating}
          className="px-4 py-2 text-xs uppercase tracking-[0.22em] bg-secondary text-background hover:bg-secondary/90 disabled:opacity-50"
          data-testid="cleanup-create-admin-btn"
        >
          {creating ? "Creating…" : "Create admin"}
        </button>
      </div>

      {/* ────────────────────────────────────────────────────────────
          2. Purge accounts
         ──────────────────────────────────────────────────────────── */}
      <div className="border border-secondary/15 bg-card/40 p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Users2 className="w-4 h-4 text-secondary" />
          <h3 className="font-serif text-xl">Purge every non-kept account</h3>
        </div>
        <p className="text-foreground/60 text-sm">
          Always keeps the admin you're currently logged in as. Add any other
          emails you want to keep below — one per line, comma-separated, or space-separated.
        </p>
        <textarea
          rows={3}
          placeholder="Vwiggs803@gmail.com&#10;another@example.com"
          value={keepRaw}
          onChange={(e) => setKeepRaw(e.target.value)}
          className="w-full bg-background border border-secondary/20 text-foreground px-3 py-2 text-sm font-mono"
          data-testid="cleanup-keep-emails"
        />
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={runPreview}
            disabled={busy}
            className="px-4 py-2 text-xs uppercase tracking-[0.22em] border border-secondary/30 text-secondary hover:bg-secondary/10 disabled:opacity-50 flex items-center gap-2"
            data-testid="cleanup-preview-btn"
          >
            <Eye className="w-3 h-3" />
            {busy && !preview ? "Loading…" : "Preview deletions (dry run)"}
          </button>
          <button
            onClick={() => setConfirmOpen(true)}
            disabled={!preview || preview.matched === 0 || busy}
            className="px-4 py-2 text-xs uppercase tracking-[0.22em] bg-rose-500/80 text-white hover:bg-rose-500 disabled:opacity-40 flex items-center gap-2"
            data-testid="cleanup-execute-btn"
          >
            <Trash2 className="w-3 h-3" />
            Execute cleanup
          </button>
        </div>

        {preview && (
          <div className="mt-4 border border-secondary/15 bg-background/40 p-4 space-y-3" data-testid="cleanup-preview-result">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="font-serif text-2xl">{preview.matched.toLocaleString()}</div>
                <div className="text-foreground/50 text-xs uppercase tracking-[0.22em]">accounts will be deleted</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-[0.22em] text-foreground/40">Keeping ({preview.keptEmails.length})</div>
                <div className="text-foreground/70 text-xs mt-1">
                  {preview.keptEmails.join(" · ")}
                </div>
              </div>
            </div>
            {preview.sample.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-foreground/50 text-[10px] uppercase tracking-[0.22em]">
                    <tr>
                      <th className="text-left px-3 py-2">Email</th>
                      <th className="text-left px-3 py-2">Name</th>
                      <th className="text-left px-3 py-2">Role</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.sample.map((u, i) => (
                      <tr key={i} className="border-t border-secondary/10">
                        <td className="px-3 py-2 font-mono text-xs">{u.email}</td>
                        <td className="px-3 py-2 text-xs text-foreground/70">{u.name || "—"}</td>
                        <td className="px-3 py-2 text-xs text-foreground/50">{u.role || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {preview.matched > preview.sample.length && (
                  <div className="text-foreground/40 text-xs mt-2 italic">
                    Showing first {preview.sample.length} of {preview.matched.toLocaleString()} — execute to delete all.
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {result && (
          <div className="mt-4 border border-emerald-500/30 bg-emerald-500/5 p-4 space-y-2" data-testid="cleanup-result">
            <div className="flex items-center gap-2 text-emerald-300">
              <Check className="w-4 h-4" />
              <span className="font-serif text-xl">Done</span>
            </div>
            <div className="text-foreground/70 text-sm">
              Deleted <b>{result.deleted}</b> account(s). Cascaded across{" "}
              <b>{Object.keys(result.cascadeCounters || {}).length}</b> collection(s).
            </div>
            {result.cascadeCounters && (
              <details className="text-xs text-foreground/50">
                <summary className="cursor-pointer">Cascade detail</summary>
                <pre className="mt-2 bg-background/40 p-2 overflow-x-auto">
                  {JSON.stringify(result.cascadeCounters, null, 2)}
                </pre>
              </details>
            )}
          </div>
        )}
      </div>

      {/* ────────────────────────────────────────────────────────────
          Confirmation modal
         ──────────────────────────────────────────────────────────── */}
      {confirmOpen && (
        <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="cleanup-confirm-modal">
          <div className="border border-rose-500/40 bg-card max-w-lg w-full p-6 space-y-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2 text-rose-300">
                <AlertTriangle className="w-5 h-5" />
                <h3 className="font-serif text-2xl">Confirm deletion</h3>
              </div>
              <button
                onClick={() => { setConfirmOpen(false); setConfirmTyped(""); }}
                className="text-foreground/60 hover:text-foreground"
                data-testid="cleanup-confirm-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="text-foreground/80 text-sm space-y-2">
              <p>
                You are about to permanently delete <b className="text-rose-300">{preview?.matched.toLocaleString()}</b> account(s)
                along with all their wallets, profiles, posts, bookings, services and notifications.
              </p>
              <p>This cannot be undone.</p>
              <p>Keeping: <span className="text-foreground/60 text-xs font-mono">{preview?.keptEmails.join(", ")}</span></p>
            </div>
            <div>
              <label className="text-foreground/60 text-xs uppercase tracking-[0.22em] block mb-2">
                Type <span className="text-rose-300 font-mono">DELETE</span> to confirm
              </label>
              <input
                type="text"
                value={confirmTyped}
                onChange={(e) => setConfirmTyped(e.target.value)}
                className="w-full bg-background border border-rose-500/30 text-foreground px-3 py-2 font-mono"
                data-testid="cleanup-confirm-input"
                autoFocus
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => { setConfirmOpen(false); setConfirmTyped(""); }}
                className="px-4 py-2 text-xs uppercase tracking-[0.22em] border border-secondary/20 text-foreground/70 hover:bg-secondary/10"
                data-testid="cleanup-confirm-cancel"
              >
                Cancel
              </button>
              <button
                onClick={execute}
                disabled={confirmTyped !== "DELETE" || busy}
                className="px-4 py-2 text-xs uppercase tracking-[0.22em] bg-rose-500 text-white hover:bg-rose-600 disabled:opacity-40 flex items-center gap-2"
                data-testid="cleanup-confirm-execute"
              >
                <Trash2 className="w-3 h-3" />
                {busy ? "Deleting…" : "Yes, delete forever"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
